import { useEffect, useRef } from "react";
import ChartCard from "../../../components/ui/ChartCard";
import KpiCard from "../../../components/ui/KpiCard";
import Tabs from "../../../components/sections/Tabs";
import LoonOpZandBinaryStackedPanel from "./LoonOpZandBinaryStackedPanel";
import LoonOpZandLikertPanel from "./LoonOpZandLikertPanel";
import LoonOpZandScorePanel from "./LoonOpZandScorePanel";
import LoonOpZandTextFrequencyTable from "./LoonOpZandTextFrequencyTable";
import MultiChoiceWithTextPanel from "./MultiChoiceWithTextPanel";
import { buildPeriodOptions } from "./chartOptions";
import { getQuestionSummary } from "./questionSummaries";
import {
  usePdfExport,
  usePdfExportSection,
} from "../../../context/PdfExportContext";
import captureElementForPdf from "../../../utils/captureElementForPdf";
import {
  getQuestion,
  selectedIssueOptions,
} from "./resultHelpers";

function Vragenlijst2Question3({
  effectQuestion,
  helpQuestion,
  helpTextQuestion,
  satisfactionQuestion,
  filters,
  mode,
  info,
  exportAll = false,
}) {
  const tabs = [
    {
      key: "effect",
      label: "Effect hulp",
      render: (
        <MultiChoiceWithTextPanel
          question={effectQuestion}
          filters={filters}
          mode={mode}
          info={info}
        />
      ),
    },
    {
      key: "received-help",
      label: "Ontvangen hulp",
      render: (
        <div className="loonopzand-dashboard__question-subtab-content">
          <MultiChoiceWithTextPanel
            question={helpQuestion}
            textQuestion={helpTextQuestion}
            filters={filters}
            mode={mode}
            info={info}
            textPanelTitle="Anders, namelijk:"
            excludeUnansweredIssues
          />
          <LoonOpZandBinaryStackedPanel
            question={satisfactionQuestion}
            filters={filters}
            info={info}
            excludeUnansweredIssues
          />
        </div>
      ),
    },
  ];

  if (exportAll) {
    return (
      <div className="loonopzand-dashboard__question-subtab-content">
        {tabs.map((tab) => (
          <section key={tab.key}>{tab.render}</section>
        ))}
      </div>
    );
  }

  return (
    <div className="loonopzand-dashboard__question-subtabs">
      <Tabs tabs={tabs} defaultActiveKey="effect" />
    </div>
  );
}

function QuestionnaireResponseOverview({ data }) {
  const kpiRef = useRef(null);
  const pdfExport = usePdfExport();
  const pdfSection = usePdfExportSection();
  const responseCount = Number(data?.summary?.response_count) || 0;
  const selectedPeriods = selectedIssueOptions(data?.filters);
  const scoreQuestion = data?.questions?.find(
    (question) => question.type === "score"
  );
  const averageScore = scoreQuestion?.stats?.average;
  const periodSeries = [
    {
      name: "Responses",
      data: selectedPeriods.map((issue) => issue.response_count),
    },
  ];

  useEffect(() => {
    if (!pdfSection?.sectionKey || !pdfExport?.registerExportable) {
      return undefined;
    }
    return pdfExport.registerExportable({
      sectionKey: pdfSection.sectionKey,
      order: (pdfSection.order || 0) - 10,
      getContent: async () => {
        if (!kpiRef.current) {
          return null;
        }
        const canvas = await captureElementForPdf(kpiRef.current);
        return {
          image: {
            src: canvas.toDataURL("image/png"),
            width: canvas.width,
            height: canvas.height,
          },
        };
      },
    });
  }, [pdfExport, pdfSection]);

  return (
    <div className="loonopzand-dashboard__content">
      <div ref={kpiRef} className="loonopzand-dashboard__kpis">
        <KpiCard icon="/icons/users-check.png" title="Responses" value={responseCount} />
        <KpiCard
          icon="/icons/filter-lines.png"
          title="Geselecteerde periodes"
          value={selectedPeriods.length}
        />
        <KpiCard
          icon="/icons/monitor.png"
          title="Gemiddeld rapportcijfer"
          value={averageScore == null ? "–" : Number(averageScore).toFixed(1)}
        />
      </div>

      <div className="loonopzand-dashboard__period-chart">
        <ChartCard
          chartKey={`loonopzand-periods-${selectedPeriods
            .map((item) => item.value)
            .join("-")}`}
          title="Aantal responses per periode"
          info="Het aantal ingevulde vragenlijsten per geselecteerde meetperiode."
          type="bar"
          options={buildPeriodOptions(selectedPeriods.map((issue) => issue.label))}
          series={periodSeries}
          height={320}
        />
      </div>
    </div>
  );
}

export default function LoonOpZandQuestionnaireTab({
  data,
  mode = "abs",
  selectedQuestion = "1",
  questionnaireSlug = "toegang-1-1",
  exportAll = false,
}) {
  const responseCount = Number(data?.summary?.response_count) || 0;
  const scoreQuestion = getQuestion(data, 623);
  const q1 = getQuestion(data, 601);
  const q1Other = getQuestion(data, 602);
  const q2 = getQuestion(data, 603);
  const q2Other = getQuestion(data, 604);
  const q3 = getQuestion(data, 605);
  const q3Other = getQuestion(data, 606);
  const likertQuestions = [607, 608, 609, 610, 611, 612]
    .map((questionId) => getQuestion(data, questionId))
    .filter(Boolean);
  const q5 = getQuestion(data, 613);
  const q6 = getQuestion(data, 614);
  const q6MainOther = getQuestion(data, 624);
  const q6Reasons = getQuestion(data, 615);
  const q6Other = getQuestion(data, 616);
  const q7Outcome = getQuestion(data, 617);
  const q7HelpText = getQuestion(data, 618);
  const q7HelpMulti = getQuestion(data, 619);
  const q7HelpOther = getQuestion(data, 620);
  const q7Satisfied = getQuestion(data, 621);
  const q8 = getQuestion(data, 622);
  const toegang12Q1 = getQuestion(data, 701);
  const toegang12Q1Text = getQuestion(data, 702);
  const toegang12Q2 = getQuestion(data, 703);
  const toegang12Q2Text = getQuestion(data, 704);
  const toegang12Q3 = getQuestion(data, 705);
  const toegang12Q3Text = getQuestion(data, 706);
  const toegang12Q4 = getQuestion(data, 707);
  const toegang12Q4Text = getQuestion(data, 708);
  const toegang12LikertQuestions = [709, 710, 711, 712, 713, 714]
    .map((questionId) => getQuestion(data, questionId))
    .filter(Boolean);
  const toegang12Q6 = getQuestion(data, 715);
  const toegang12Q7 = getQuestion(data, 716);
  const toegang12Q7Reasons = getQuestion(data, 717);
  const toegang12Q7Other = getQuestion(data, 718);
  const toegang12Q8Outcome = getQuestion(data, 719);
  const toegang12Q8HelpText = getQuestion(data, 720);
  const toegang12Q8HelpMulti = getQuestion(data, 721);
  const toegang12Q8HelpOther = getQuestion(data, 722);
  const toegang12Q8Satisfied = getQuestion(data, 723);
  const toegang12Q9 = getQuestion(data, 724);
  const toegang12Q10 = getQuestion(data, 725);
  const vragenlijst2Q1 = getQuestion(data, 801);
  const vragenlijst2Q2 = getQuestion(data, 802);
  const vragenlijst2Q2Text = getQuestion(data, 803);
  const vragenlijst2Q3Effect = getQuestion(data, 804);
  const vragenlijst2Q3Help = getQuestion(data, 805);
  const vragenlijst2Q3HelpText = getQuestion(data, 806);
  const vragenlijst2Q3Satisfied = getQuestion(data, 807);
  const vragenlijst2Q4 = getQuestion(data, 808);
  const vragenlijst2Q5 = getQuestion(data, 809);
  const vragenlijst3Q1 = getQuestion(data, 901);
  const vragenlijst3Q2 = getQuestion(data, 904);
  const vragenlijst3Q3 = getQuestion(data, 905);
  const summaryInfo = getQuestionSummary(questionnaireSlug, selectedQuestion);
  if (!data) {
    return null;
  }

  if (responseCount === 0) {
    return (
      <div className="loonopzand-dashboard__empty">
        Geen responses gevonden voor de geselecteerde periode.
      </div>
    );
  }

  if (selectedQuestion === "overview") {
    return <QuestionnaireResponseOverview data={data} />;
  }

  if (questionnaireSlug === "vragenlijst-3") {
    return (
      <div className="loonopzand-dashboard__content">
        <div className="loonopzand-dashboard__question-charts">
          {selectedQuestion === "1" && vragenlijst3Q1 && (
            <MultiChoiceWithTextPanel
              question={vragenlijst3Q1}
              filters={data.filters}
              mode={mode}
              info={summaryInfo}
            />
          )}
          {selectedQuestion === "2" && vragenlijst3Q2 && (
            <LoonOpZandTextFrequencyTable question={vragenlijst3Q2} info={summaryInfo} />
          )}
          {selectedQuestion === "3" && vragenlijst3Q3 && (
            <LoonOpZandScorePanel
              question={vragenlijst3Q3}
              filters={data.filters}
              mode={mode}
              info={summaryInfo}
            />
          )}
        </div>
      </div>
    );
  }

  if (questionnaireSlug === "vragenlijst-2") {
    return (
      <div className="loonopzand-dashboard__content">
        <div className="loonopzand-dashboard__question-charts">
          {selectedQuestion === "1" && vragenlijst2Q1 && (
            <MultiChoiceWithTextPanel
              question={vragenlijst2Q1}
              filters={data.filters}
              mode={mode}
              info={summaryInfo}
            />
          )}
          {selectedQuestion === "2" && vragenlijst2Q2 && (
            <MultiChoiceWithTextPanel
              question={vragenlijst2Q2}
              textQuestion={vragenlijst2Q2Text}
              filters={data.filters}
              mode={mode}
              info={summaryInfo}
              textPanelTitle="Anders, namelijk:"
            />
          )}
          {selectedQuestion === "3" &&
            vragenlijst2Q3Effect &&
            vragenlijst2Q3Help &&
            vragenlijst2Q3Satisfied && (
              <Vragenlijst2Question3
                effectQuestion={vragenlijst2Q3Effect}
                helpQuestion={vragenlijst2Q3Help}
                helpTextQuestion={vragenlijst2Q3HelpText}
                satisfactionQuestion={vragenlijst2Q3Satisfied}
                filters={data.filters}
                mode={mode}
                info={summaryInfo}
                exportAll={exportAll}
              />
            )}
          {selectedQuestion === "4" && vragenlijst2Q4 && (
            <LoonOpZandTextFrequencyTable question={vragenlijst2Q4} info={summaryInfo} />
          )}
          {selectedQuestion === "5" && vragenlijst2Q5 && (
            <LoonOpZandScorePanel
              question={vragenlijst2Q5}
              filters={data.filters}
              mode={mode}
              info={summaryInfo}
            />
          )}
        </div>
      </div>
    );
  }

  if (questionnaireSlug === "toegang-1-2") {
    return (
      <div className="loonopzand-dashboard__content">
        <div className="loonopzand-dashboard__question-charts">
          {selectedQuestion === "1" && toegang12Q1 && (
            <MultiChoiceWithTextPanel
              question={toegang12Q1}
              textQuestion={toegang12Q1Text}
              filters={data.filters}
              mode={mode}
              info={summaryInfo}
              textPanelTitle="De ondersteuning heeft mij niet geholpen, omdat:"
            />
          )}
          {selectedQuestion === "2" && toegang12Q2 && (
            <MultiChoiceWithTextPanel
              question={toegang12Q2}
              textQuestion={toegang12Q2Text}
              filters={data.filters}
              mode={mode}
              info={summaryInfo}
              textPanelTitle="Anders, namelijk:"
            />
          )}
          {selectedQuestion === "3" && toegang12Q3 && (
            <MultiChoiceWithTextPanel
              question={toegang12Q3}
              textQuestion={toegang12Q3Text}
              filters={data.filters}
              mode={mode}
              info={summaryInfo}
              textPanelTitle="Anders, namelijk:"
            />
          )}
          {selectedQuestion === "4" && toegang12Q4 && (
            <MultiChoiceWithTextPanel
              question={toegang12Q4}
              textQuestion={toegang12Q4Text}
              filters={data.filters}
              mode={mode}
              info={summaryInfo}
              textPanelTitle="Anders, namelijk:"
            />
          )}
          {selectedQuestion === "5" && toegang12LikertQuestions.length === 6 && (
            <LoonOpZandLikertPanel
              questions={toegang12LikertQuestions}
              filters={data.filters}
              title="5. In hoeverre bent u het eens met de volgende stellingen?"
              info={summaryInfo}
            />
          )}
          {selectedQuestion === "6" && toegang12Q6 && (
            <LoonOpZandTextFrequencyTable question={toegang12Q6} info={summaryInfo} />
          )}
          {selectedQuestion === "7" && toegang12Q7 && (
            <LoonOpZandBinaryStackedPanel
              question={toegang12Q7}
              filters={data.filters}
              info={summaryInfo}
            />
          )}
          {selectedQuestion === "7" && toegang12Q7Reasons && (
            <MultiChoiceWithTextPanel
              question={toegang12Q7Reasons}
              textQuestion={toegang12Q7Other}
              filters={data.filters}
              mode={mode}
              info={summaryInfo}
              textPanelTitle="Anders, namelijk:"
            />
          )}
          {selectedQuestion === "8" && toegang12Q8Outcome && (
            <LoonOpZandBinaryStackedPanel
              question={toegang12Q8Outcome}
              filters={data.filters}
              colors={["#22c55e", "#f59e0b", "#ef4444"]}
              info={summaryInfo}
            />
          )}
          {selectedQuestion === "8" && toegang12Q8HelpMulti && (
            <MultiChoiceWithTextPanel
              question={toegang12Q8HelpMulti}
              filters={data.filters}
              mode={mode}
              info={summaryInfo}
              textPanelTitle="Antwoorden ontvangen hulp"
              textSections={[
                {
                  question: toegang12Q8HelpText,
                  title: "Vrije antwoorden oudere vragenlijst",
                },
                {
                  question: toegang12Q8HelpOther,
                  title: "Anders, namelijk:",
                },
              ]}
              excludeUnansweredIssues
            />
          )}
          {selectedQuestion === "8" && toegang12Q8Satisfied && (
            <LoonOpZandBinaryStackedPanel
              question={toegang12Q8Satisfied}
              filters={data.filters}
              info={summaryInfo}
              excludeUnansweredIssues
            />
          )}
          {selectedQuestion === "9" && toegang12Q9 && (
            <LoonOpZandTextFrequencyTable question={toegang12Q9} info={summaryInfo} />
          )}
          {selectedQuestion === "10" && toegang12Q10 && (
            <LoonOpZandScorePanel
              question={toegang12Q10}
              filters={data.filters}
              mode={mode}
              info={summaryInfo}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="loonopzand-dashboard__content">
      <div className="loonopzand-dashboard__question-charts">
        {selectedQuestion === "1" && q1 && (
          <MultiChoiceWithTextPanel
            question={q1}
            textQuestion={q1Other}
            filters={data.filters}
            mode={mode}
            info={summaryInfo}
          />
        )}
        {selectedQuestion === "2" && q2 && (
          <MultiChoiceWithTextPanel
            question={q2}
            textQuestion={q2Other}
            filters={data.filters}
            mode={mode}
            info={summaryInfo}
          />
        )}
        {selectedQuestion === "3" && q3 && (
          <MultiChoiceWithTextPanel
            question={q3}
            textQuestion={q3Other}
            filters={data.filters}
            mode={mode}
            info={summaryInfo}
          />
        )}
        {selectedQuestion === "4" && likertQuestions.length === 6 && (
          <LoonOpZandLikertPanel
            questions={likertQuestions}
            filters={data.filters}
            info={summaryInfo}
          />
        )}
        {selectedQuestion === "5" && q5 && (
          <LoonOpZandTextFrequencyTable question={q5} info={summaryInfo} />
        )}
        {selectedQuestion === "6" && q6 && (
          <LoonOpZandBinaryStackedPanel
            question={q6}
            textQuestion={q6MainOther}
            filters={data.filters}
            info={summaryInfo}
          />
        )}
        {selectedQuestion === "6" && q6Reasons && (
          <MultiChoiceWithTextPanel
            question={q6Reasons}
            textQuestion={q6Other}
            filters={data.filters}
            mode={mode}
            info={summaryInfo}
            textPanelTitle="Anders, namelijk (indien met nee beantwoord):"
          />
        )}
        {selectedQuestion === "7" && q7Outcome && (
          <LoonOpZandBinaryStackedPanel
            question={q7Outcome}
            filters={data.filters}
            colors={["#22c55e", "#f59e0b", "#ef4444"]}
            info={summaryInfo}
          />
        )}
        {selectedQuestion === "7" && q7HelpMulti && (
          <MultiChoiceWithTextPanel
            question={q7HelpMulti}
            filters={data.filters}
            mode={mode}
            info={summaryInfo}
            textPanelTitle="Antwoorden ontvangen hulp"
            textSections={[
              {
                question: q7HelpText,
                title: "Vrije antwoorden oudere vragenlijst",
              },
              {
                question: q7HelpOther,
                title: "Anders, namelijk:",
              },
            ]}
            excludeUnansweredIssues
          />
        )}
        {selectedQuestion === "7" && q7Satisfied && (
          <LoonOpZandBinaryStackedPanel
            question={q7Satisfied}
            textQuestion={q7Satisfied}
            filters={data.filters}
            textPanelTitle="Overige tekstreacties"
            info={summaryInfo}
          />
        )}
        {selectedQuestion === "8" && q8 && (
          <LoonOpZandTextFrequencyTable question={q8} info={summaryInfo} />
        )}
        {selectedQuestion === "9" && scoreQuestion && (
          <LoonOpZandScorePanel
            question={scoreQuestion}
            filters={data.filters}
            mode={mode}
            info={summaryInfo}
          />
        )}
      </div>
    </div>
  );
}
